import axiosInstance from './axiosInstance';
import { getDepartmentChildren } from './shift-logAPI';

const API_URL = process.env.REACT_APP_API_URL;

const normalizeDepartmentIds = (value) => {
  if (Array.isArray(value)) {
    return value
      .map((id) => Number(id))
      .filter((id) => Number.isInteger(id) && id > 0);
  }
  const numeric = Number(value);
  return Number.isInteger(numeric) && numeric > 0 ? [numeric] : [];
};

const resolveDepartmentIds = async (value) => {
  const initialIds = normalizeDepartmentIds(value);
  if (!initialIds.length) {
    return [];
  }
  const resolved = new Set();
  await Promise.all(
    initialIds.map(async (id) => {
      try {
        const response = await getDepartmentChildren(id);
        const ids = Array.isArray(response)
          ? response
          : Array.isArray(response?.data)
            ? response.data
            : Array.isArray(response?.departmentIds)
              ? response.departmentIds
              : [];
        const normalizedIds = ids
          .map((childId) => Number(childId))
          .filter((childId) => Number.isInteger(childId) && childId > 0);
        if (normalizedIds.length) {
          normalizedIds.forEach((childId) => resolved.add(childId));
        } else {
          resolved.add(id);
        }
      } catch (error) {
        console.error('Error resolving department children:', error.response?.data || error);
        resolved.add(id);
      }
    }),
  );
  return Array.from(resolved);
};

const serializeParams = (params) => {
  const query = new URLSearchParams();
  for (const key in params) {
    const value = params[key];
    if (Array.isArray(value)) {
      value.forEach((v) => query.append(key, v));
    } else if (value !== undefined && value !== null) {
      query.append(key, value);
    }
  }
  return query.toString();
};

// GET all events
export const getEvents = async () => {
  try {
    const response = await axiosInstance.get(`${API_URL}/events/all`);
    return response.data;
  } catch (error) {
    console.error("Error fetching events:", error.response?.data || error);
    throw error;
  }
};

// GET a single event
export const getEventsById = async (id) => {
  try {
    const response = await axiosInstance.get(`${API_URL}/events/${id}`);
    return response.data;
  } catch (error) {
    console.error("Error fetching events:", error.response?.data || error);
    throw error;
  }
};

// GET many events by ids
export const getEventsByIds = async (eventIds = []) => {
  if (!eventIds.length) {
    return [];
  }

  const response = await axiosInstance.get(`${API_URL}/events/by-ids`, {
    params: { eventIds: eventIds.join(',') },
  });
  return response.data;
};

// CREATE a new event
export const createEvents = async (eventsData) => {
  try {
    const response = await axiosInstance.post(`${API_URL}/events`, eventsData);
    return response.data;
  } catch (error) {
    console.error("Error creating events:", error.response?.data || error);
    throw error;
  }
};

// UPDATE an existing event
export const updateEvents = async (id, eventsData) => {
  try {
    const response = await axiosInstance.put(`${API_URL}/events/${id}`, eventsData);
    return response.data;
  } catch (error) {
    const errorCode = error?.response?.data?.error;
    if (errorCode !== 'EVENT_DATE_OVERLAP') {
      console.error("Error updating events:", error.response?.data || error);
    }
    throw error;
  }
};

// DELETE an event
export const deleteEvents = async (id) => {
  try {
    const response = await axiosInstance.delete(`${API_URL}/events/${id}`);
    return response.data;
  } catch (error) {
    console.error("Error deleting events:", error.response?.data || error);
    throw error;
  }
};

// GET paginated events
export const getPaginatedEvents = async ({
  page = 1,
  pageSize = 25,
  search = '',
  assetId,
  assetGroupId,
  assetClassId,
  eventStatusId,
  startDate,
  endDateStart,
  endDate,
  departmentId,
  eventTypeId,
  systemId,
  eventResponsibilityId,
  plannedStartDate,
  plannedEndDate,
  sortField,
  sortOrder,
} = {}) => {
  const resolvedDepartmentIds = await resolveDepartmentIds(departmentId);
  const response = await axiosInstance.get(`${API_URL}/events`, {
    params: {
      page,
      pageSize,
      search,
      assetId,
      assetGroupId,
      assetClassId,
      eventStatusId,
      startDate,
      endDateStart,
      endDate,
      eventTypeId,
      systemId,
      eventResponsibilityId,
      plannedStartDate,
      plannedEndDate,
      sortField,
      sortOrder,
      departmentId: resolvedDepartmentIds.length ? resolvedDepartmentIds : undefined,
    },
    paramsSerializer: serializeParams,
  });
  return response.data;
};

export const exportEventsPdfReport = async ({
  page = 1,
  pageSize = 1000,
  search = '',
  assetId,
  assetGroupId,
  assetClassId,
  eventStatusId,
  eventStatus,
  startDate,
  endDateStart,
  endDate,
  departmentId,
  eventTypeId,
  systemId,
  eventResponsibilityId,
  plannedStartDate,
  plannedEndDate,
  sortField,
  sortOrder,
} = {}) => {
  const resolvedDepartmentIds = await resolveDepartmentIds(departmentId);
  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const response = await axiosInstance.get(`${API_URL}/events/report/pdf`, {
    responseType: 'blob',
    params: {
      page,
      pageSize,
      search,
      assetId,
      assetGroupId,
      assetClassId,
      eventStatusId,
      eventStatus,
      startDate,
      endDateStart,
      endDate,
      eventTypeId,
      systemId,
      eventResponsibilityId,
      plannedStartDate,
      plannedEndDate,
      sortField,
      sortOrder,
      timeZone,
      departmentId: resolvedDepartmentIds.length ? resolvedDepartmentIds : undefined,
    },
    paramsSerializer: serializeParams,
  });
  return response.data;
};
